import { useEffect, useMemo, useState } from 'react';
import { CAMPAIGN_PRESETS, campaignTag, type CampaignPreset, type SourcesReportDto } from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { TextField, ToggleField } from '@/components/fields';
import { AnnotatedSection, Page, Section, Table, TableScroll, Td, Th } from '@/components/page';
import { Alert, ButtonLink, Card, CopyButton, EmptyState, Skeleton, cx } from '@/components/ui';
import { CoinsIcon } from '@/components/icons';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { categorySlugOptions, productSlugOptions } from '@/lib/options';
import { campaignUrl } from '@/lib/campaign-url';
import { RANGE_PRESETS } from '@/lib/date-range';
import { count, money } from '@/lib/format';
import { storefrontUrl } from '@/lib/storefront-url';

type Target = 'home' | 'product' | 'category';
const TARGETS: Target[] = ['home', 'product', 'category'];

/** Products and categories by their store link: the slug is what the address needs. */
const bySlug = { product: productSlugOptions, category: categorySlugOptions };

/** A paid post files under the paid medium of its kind (ADR-097). */
function paidMedium(medium: string): string {
  return medium === 'social' ? 'paid_social' : medium === 'cpc' ? 'cpc' : 'paid';
}

/**
 * Marketing → Campaign links (ADR-097): a store link tagged for where it will
 * be posted, so the order it brings is filed under its campaign — and the
 * campaigns of the last thirty days beside it.
 */
export function CampaignLinksPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [target, setTarget] = useState<Target>('home');
  const [slug, setSlug] = useState('');
  const [language, setLanguage] = useState<'ar' | 'en'>(locale);
  const [preset, setPreset] = useState<CampaignPreset>('instagram');
  const [source, setSource] = useState('instagram');
  const [medium, setMedium] = useState('social');
  const [paid, setPaid] = useState(false);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');

  const path = target === 'home' ? '/' : slug ? `/${target === 'product' ? 'products' : 'categories'}/${slug}` : null;
  const base = path ? storefrontUrl(language, path) : null;
  const campaign = campaignTag(name);
  const link = useMemo(
    () => (base ? campaignUrl(base, { source, medium: paid ? paidMedium(medium) : medium, campaign, content }) : null),
    [base, source, medium, paid, campaign, content],
  );

  const choose = (key: CampaignPreset) => {
    const picked = CAMPAIGN_PRESETS.find((entry) => entry.key === key)!;
    setPreset(key);
    setSource(picked.source);
    setMedium(picked.medium);
  };

  return (
    <Page title={t('campaignLinks')} subtitle={t('campaignLinksHint')}>
      <div className="flex flex-col gap-6">
        <Card className="flex flex-col gap-6 p-5">
          <AnnotatedSection title={t('campaignLinkTo')} description={t('campaignLinkToHint')}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                {TARGETS.map((entry) => (
                  <Chip key={entry} on={target === entry} onClick={() => (setTarget(entry), setSlug(''))}>
                    {t(`campaignTarget_${entry}`)}
                  </Chip>
                ))}
                <span className="mx-1 w-px bg-line" aria-hidden />
                {(['ar', 'en'] as const).map((entry) => (
                  <Chip key={entry} on={language === entry} onClick={() => setLanguage(entry)}>
                    {t(`feedLanguage_${entry}`)}
                  </Chip>
                ))}
              </div>
              {target !== 'home' ? (
                <AsyncSelect id="campaign-target" value={slug} placeholder={t(`campaignTarget_${target}`)} fetchPage={bySlug[target].page} fetchOne={bySlug[target].one} onChange={(next) => setSlug(next)} />
              ) : null}
            </div>
          </AnnotatedSection>

          <AnnotatedSection title={t('campaignWhere')} description={t('campaignWhereHint')}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                {CAMPAIGN_PRESETS.map((entry) => (
                  <Chip key={entry.key} on={preset === entry.key} onClick={() => choose(entry.key)}>
                    {t(`campaignPreset_${entry.key}`)}
                  </Chip>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <TextField label={t('campaignSource')} latin value={source} onChange={(next) => setSource(next.trim().toLowerCase())} />
                <TextField label={t('campaignMedium')} latin value={medium} onChange={(next) => setMedium(next.trim().toLowerCase())} />
              </div>
              <ToggleField label={t('campaignPaid')} hint={t('campaignPaidHint')} checked={paid} onChange={setPaid} />
            </div>
          </AnnotatedSection>

          <AnnotatedSection title={t('campaignLinkName')} description={t('campaignLinkNameHint')}>
            <div className="flex flex-col gap-3">
              <TextField label={t('campaignLinkName')} value={name} placeholder={t('campaignLinkNamePlaceholder')} onChange={setName} />
              <TextField label={t('campaignContent')} hint={t('campaignContentHint')} latin value={content} onChange={setContent} />
            </div>
          </AnnotatedSection>

          <div className="flex flex-col gap-2 rounded-card border border-brand-500/25 bg-brand-50/40 p-4">
            <span className="text-sm font-medium text-ink">{t('campaignYourLink')}</span>
            {link && campaign ? (
              <div className="flex items-start gap-2">
                {/* Read as written — an Arabic campaign name stays legible; what is copied is the encoded link. */}
                <code dir="ltr" className="min-w-0 flex-1 break-all rounded bg-surface px-2 py-1.5 text-start font-mono text-xs text-ink">
                  {readable(link)}
                </code>
                <CopyButton value={link} label={t('copy')} copiedLabel={t('copied')} />
              </div>
            ) : (
              <p className="text-sm text-ink-muted">{!base && path ? t('campaignNoStoreUrl') : t('campaignFillIn')}</p>
            )}
          </div>
        </Card>

        <RecentCampaigns />
      </div>
    </Page>
  );
}

/** A link as a person reads it: encoded letters shown as themselves. */
function readable(link: string): string {
  try {
    return decodeURI(link);
  } catch {
    return link;
  }
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cx(
        'rounded-full border px-3 py-1.5 text-sm transition-colors',
        on ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line text-ink hover:border-brand-300',
      )}
    >
      {children}
    </button>
  );
}

/** The last thirty days' campaigns by revenue: what the links made. */
function RecentCampaigns() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [report, setReport] = useState<SourcesReportDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    withSession(() => adminApi.reports.sources(RANGE_PRESETS.last30()))
      .then((loaded) => current && setReport(loaded))
      .catch((failure: unknown) => current && setError(errorMessage(failure, 'Could not load the campaigns.')));
    return () => {
      current = false;
    };
  }, []);

  return (
    <Section
      title={t('campaignRecent')}
      subtitle={t('campaignRecentHint')}
      flush
      action={
        <ButtonLink to="/reports" variant="ghost" size="sm">
          {t('reports')}
        </ButtonLink>
      }
    >
      {error ? <Alert>{error}</Alert> : null}
      {!report && !error ? (
        <Skeleton className="m-4 h-40" />
      ) : report && report.campaigns.length === 0 ? (
        <div className="border-t border-line">
          <EmptyState title={t('sourceCampaignsEmpty')} icon={<CoinsIcon />} />
        </div>
      ) : report ? (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>{t('sourceCampaign')}</Th>
                <Th align="end">{t('orders')}</Th>
                <Th align="end">{t('revenue')}</Th>
              </tr>
            </thead>
            <tbody>
              {report.campaigns.slice(0, 10).map((row, index) => (
                <tr key={index}>
                  <Td>
                    <span className="block font-medium text-ink">{row.campaign ?? '—'}</span>
                    <span dir="ltr" className="block text-start text-xs text-ink-muted">
                      {[row.source, row.medium].filter(Boolean).join(' / ')}
                    </span>
                  </Td>
                  <Td align="end">{count(row.orders, locale)}</Td>
                  <Td align="end">{money(row.revenue, locale)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      ) : null}
    </Section>
  );
}
